import { reactive, watch } from 'vue'
import { serialize } from 'object-to-formdata'
import Errors from '@/errors'
import { deepCopy, hasFiles } from '@/utils'

export default function Form(data = {}) {
    let ignoreKeys = ['busy', 'successful', 'errors', 'progress', 'originalData'];

    let form = reactive({
        originalData: {},
        busy: false,
        isDirty: false,
        successful: false,
        errors: new Errors(),
        progress: {},
        init(data){
            this.originalData = Object.assign({}, this.originalData, deepCopy(data))

            Object.assign(this, data)
        },
        fill(data = {}){
            this.keys().forEach((key) => {
                if (_.has(data, key)) {
                    (this)[key] = data[key]
                }
            })
        },
        data(){
            return Object.keys(this.originalData).reduce((data, key) => (
                { ...data, [key]: (this)[key] }
            ), {})
        },
        keys(){
            return Object.keys(this).filter(key => !ignoreKeys.includes(key))
        },
        startProcessing(){
            this.errors.clear()
            this.busy = true
            this.successful = false
            this.progress = undefined
        },
        finishProcessing(){
            this.busy = false
            this.isDirty = false
            this.successful = true
            this.progress = undefined
        },
        reset(withData = false){
            this.errors.clear()
            this.successful = false
            this.progress = undefined

            if (withData) {
                this.resetData()
            }
        },
        resetData(){
            Object.keys(this.originalData).filter(key => !ignoreKeys.includes(key)).forEach((key) => {
                (this)[key] = deepCopy(this.originalData[key])
            })
        },
        get(url, config = {}){
            return this.submit('get', url, config)
        },
        post(url, config = {}){
            return this.submit('post', url, config)
        },
        patch(url, config = {}){
            return this.submit('patch', url, config)
        },
        delete(url, config = {}){
            return this.submit('delete', url, config)
        },
        submit(method, url, config = {}){
            this.startProcessing()

            config = {
                data: {},
                params: {},
                url: this.route(url),
                method: method,
                onUploadProgress: this.handleUploadProgress.bind(this),
                ...config
            }

            if (method.toLowerCase() === 'get') {
                config.params = { ...this.data(), ...config.params }
            } else {
                config.data = { ...this.data(), ...config.data }

                if (hasFiles(config.data)) {
                    config.transformRequest = [data => serialize(data)]
                }
            }

            return new Promise((resolve, reject) => {
                axios.request(config)
                    .then((response) => {
                        this.finishProcessing()
                        resolve(response)
                    })
                    .catch((error) => {
                        this.handleErrors(error)
                        reject(error)
                    })
            })
        },
        handleErrors(error) {
            this.busy = false
            this.progress = undefined

            if (error.response) {
                this.errors.set(this.extractErrors(error.response))
            }
        },
        extractErrors(response) {
            if (!response.data || typeof response.data !== 'object') {
                return { error: 'Something went wrong. Please try again.' }
            }

            if (response.data.errors) {
                return { ...response.data.errors }
            }

            if (response.data.message) {
                return { error: response.data.message }
            }

            return { ...response.data }
        },
        handleUploadProgress(event) {
            this.progress = Object.assign({}, {
                total: event.total,
                loaded: event.loaded,
                percentage: Math.round((event.loaded * 100) / event.total)
            })
        },
        route(name, parameters = {}) {
            let url = name
            let routes = {}

            if (Object.prototype.hasOwnProperty.call(routes, name)) {
                url = decodeURI(routes[name])
            }

            if (typeof parameters !== 'object') {
                parameters = { id: parameters }
            }

            Object.keys(parameters).forEach((key) => {
                url = url.replace(`{${key}}`, parameters[key])
            })

            return url
        },
        onKeydown(event) {
            const target = event.target

            if (target.name) {
                this.errors.clear(target.name)
            }
        }
    })

    form.init(data)

    watch(form, newValue => {
        form.isDirty = !_.isEqual(form.data(), form.originalData)
    }, { immediate: true, deep: true })

    return form
}
